import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, Phone, Mail, MapPin, Building2, User, RefreshCw, ExternalLink, Lock, LogOut, KeyRound, ShieldCheck, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "closed"];
const STATUS_STYLE = {
  new: "bg-gold/15 text-gold border-gold/40",
  contacted: "bg-mint/15 text-mint border-mint/40",
  quoted: "bg-kraft/15 text-kraft border-kraft/30",
  closed: "bg-white/5 text-kraft/50 border-white/15",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"/>
      <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"/>
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
    </svg>
  );
}

function QuoteCard({ q, onStatus }) {
  return (
    <div
      className="rounded-2xl border border-mint/15 bg-moss/25 p-6 clip-corner hover:border-mint/35 transition-colors"
      data-testid={`quote-card-${q.rfq_ref}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <p className="font-mono text-sm tracking-[0.2em] text-mint">{q.rfq_ref}</p>
          <span
            className={`font-mono text-[9px] tracking-[0.2em] uppercase border rounded-full px-2.5 py-1 ${STATUS_STYLE[q.status] || STATUS_STYLE.new}`}
            data-testid={`quote-status-${q.rfq_ref}`}
          >
            {q.status}
          </span>
        </div>
        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-kraft/40">
          {new Date(q.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mb-4">
        <p className="font-display font-medium text-lg text-kraft">
          {q.buyer_type === "business" ? <Building2 size={15} className="inline mr-1.5 -mt-0.5 text-mintsoft" /> : <User size={15} className="inline mr-1.5 -mt-0.5 text-mintsoft" />}
          {q.name}
          {q.business_name && <span className="text-kraft/50 text-sm font-normal"> · {q.business_name}</span>}
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-2 mb-5 font-mono text-[11px] text-kraft/70">
        <p className="flex items-center gap-2" data-testid={`quote-phone-${q.rfq_ref}`}><Phone size={12} className="text-mintsoft/70" />{q.phone}</p>
        {q.email && <p className="flex items-center gap-2 truncate"><Mail size={12} className="text-mintsoft/70" />{q.email}</p>}
        <p className="flex items-center gap-2"><MapPin size={12} className="text-mintsoft/70" />{q.delivery_area}</p>
      </div>

      <div className="rounded-xl border border-mint/10 bg-forest/50 divide-y divide-mint/10 mb-4">
        {q.items.map((it, i) => (
          <div key={i} className="flex flex-wrap justify-between gap-x-6 gap-y-1 px-4 py-2.5">
            <span className="text-sm text-kraft/85">{it.name}</span>
            <span className="font-mono text-[11px] text-mintsoft/90">
              ×{it.qty}{it.spec ? ` — ${it.spec}` : ""}
            </span>
          </div>
        ))}
      </div>

      {q.notes && (
        <p className="text-sm text-kraft/60 italic mb-5">“{q.notes}”</p>
      )}

      {q.artwork && (
        <a
          href={`/api/files/${q.artwork}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`quote-artwork-${q.rfq_ref}`}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-mint border border-mint/30 rounded-full px-3.5 py-1.5 mb-4 hover:bg-mint hover:text-forest transition-colors"
        >
          <Paperclip size={12} /> View artwork
        </a>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-kraft/40 mr-1">Set status</span>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => onStatus(q.rfq_ref, s)}
            data-testid={`status-btn-${q.rfq_ref}-${s}`}
            className={`rounded-full px-3.5 h-8 font-mono text-[10px] tracking-[0.15em] uppercase border transition-colors ${
              q.status === s
                ? "bg-mint text-forest border-mint font-semibold"
                : "border-mint/25 text-kraft/60 hover:border-mint hover:text-mint"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function LoginGate({ onLogin }) {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    searchParams.get("auth") === "failed" ? "Google sign-in is only for the shop owner's account." : ""
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/login", { password });
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="pt-40 pb-24 min-h-screen flex items-start justify-center" data-testid="admin-login-page">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-sm w-full mx-5 rounded-3xl border border-mint/25 bg-moss/40 p-9 text-center"
        data-testid="admin-login-form"
      >
        <div className="w-14 h-14 mx-auto rounded-2xl border border-mint/30 bg-forest/60 grid place-items-center mb-5">
          <Lock size={22} className="text-mint" />
        </div>
        <h1 className="font-display font-semibold text-2xl text-kraft mb-2">Quote Inbox</h1>
        <p className="text-sm text-kraft/55 mb-7">Owner only — sign in to open requests.</p>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="w-full rounded-full bg-kraft text-coal font-semibold h-12 flex items-center justify-center gap-2.5 hover:bg-white transition-colors mb-5"
        >
          <GoogleIcon /> Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-5">
          <span className="flex-1 h-px bg-mint/15" />
          <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-kraft/40">or password</span>
          <span className="flex-1 h-px bg-mint/15" />
        </div>

        <div className="relative mb-4">
          <KeyRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-mintsoft/60" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Password"
            className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 pl-11 pr-4 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint"
          />
        </div>

        {error && (
          <p className="mb-4 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-xl px-4 py-3" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="w-full rounded-full bg-mint text-forest font-semibold h-12 hover:bg-kraft transition-colors disabled:opacity-60"
        >
          {busy ? "Unlocking…" : "Open the inbox"}
        </button>
      </motion.form>
    </main>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) {
      setError("New password needs at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The new passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Inbox password updated");
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-5" data-testid="change-password-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        data-testid="change-password-backdrop"
      />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-sm w-full rounded-3xl border border-mint/25 bg-moss/90 p-8"
        data-testid="change-password-form"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl border border-mint/30 bg-forest/60 grid place-items-center">
              <ShieldCheck size={18} className="text-mint" />
            </div>
            <h2 className="font-display font-semibold text-xl text-kraft">Change password</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="change-password-cancel" className="text-kraft/50 hover:text-kraft transition-colors p-1">
            <X size={18} />
          </button>
        </div>

        {[
          ["Current password", cur, setCur, "admin-cp-current"],
          ["New password", next, setNext, "admin-cp-new"],
          ["Repeat new password", confirm, setConfirm, "admin-cp-confirm"],
        ].map(([label, val, setter, tid]) => (
          <div key={tid} className="mb-4">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">{label}</label>
            <input
              type="password"
              value={val}
              onChange={(e) => setter(e.target.value)}
              required
              data-testid={tid}
              className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft focus:outline-none focus:border-mint"
            />
          </div>
        ))}

        {error && (
          <p className="mb-4 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-xl px-4 py-3" data-testid="change-password-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !cur || !next || !confirm}
          data-testid="change-password-submit"
          className="w-full rounded-full bg-mint text-forest font-semibold h-12 hover:bg-kraft transition-colors disabled:opacity-60"
        >
          {busy ? "Updating…" : "Update password"}
        </button>
        <p className="mt-4 text-center font-mono text-[9px] tracking-[0.18em] uppercase text-kraft/40">
          Takes effect immediately — old password stops working
        </p>
      </motion.form>
    </div>
  );
}

export default function AdminQuotes() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null); // null = checking, false = logged out
  const [me, setMe] = useState(location.state?.user || null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showPw, setShowPw] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/quotes")
      .then((r) => setQuotes(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) setAuth(false);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (location.state?.user) return; // user passed from AuthCallback
    if (location.hash?.includes("session_id=")) return; // AuthCallback is handling sign-in
    api
      .get("/auth/me")
      .then((r) => {
        setMe(r.data);
        setAuth(true);
      })
      .catch(() => setAuth(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth === true) load();
  }, [auth]);

  const onStatus = async (ref, status) => {
    setQuotes((prev) => prev.map((q) => (q.rfq_ref === ref ? { ...q, status } : q)));
    try {
      await api.post(`/quotes/${ref}/status`, { status });
    } catch (err) {
      if (err?.response?.status === 401) setAuth(false);
      else load();
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setMe(null);
    setAuth(false);
  };

  if (auth === null) {
    return (
      <main className="pt-40 pb-24 min-h-screen" data-testid="admin-loading">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-mintsoft/70 text-center">Checking the lock…</p>
      </main>
    );
  }

  if (auth === false) {
    return <LoginGate onLogin={() => setAuth(true)} />;
  }

  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  const counts = Object.fromEntries(STATUSES.map((s) => [s, quotes.filter((q) => q.status === s).length]));

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-3">
              Quote Inbox
            </p>
            <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-kraft">
              Requests from the shop
            </h1>
            {me?.email && (
              <p className="mt-3 font-mono text-[10px] tracking-[0.15em] uppercase text-mintsoft/70 flex items-center gap-2" data-testid="admin-signed-in-as">
                {me.picture && <img src={me.picture} alt="" className="w-4 h-4 rounded-full" />}
                Signed in as {me.email}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-mint/30 text-kraft/80 px-5 h-10 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-mint hover:text-mint transition-colors"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              onClick={() => setShowPw(true)}
              data-testid="admin-change-password-btn"
              className="inline-flex items-center gap-2 rounded-full border border-mint/30 text-kraft/80 px-5 h-10 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-mint hover:text-mint transition-colors"
            >
              <ShieldCheck size={13} /> Password
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-mint/30 text-kraft/80 px-5 h-10 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut size={13} /> Lock
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-8 mb-10" data-testid="admin-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`rounded-full px-4 h-9 font-mono text-[10px] tracking-[0.15em] uppercase border transition-colors ${
                filter === s
                  ? "bg-mint text-forest border-mint font-semibold"
                  : "border-mint/25 text-kraft/60 hover:border-mint hover:text-mint"
              }`}
            >
              {s}{s !== "all" && ` · ${counts[s] || 0}`}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-mintsoft/70 text-center py-20">
            Opening the inbox…
          </p>
        ) : shown.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center" data-testid="admin-empty-state">
            <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-mint/30 grid place-items-center">
              <Inbox size={24} className="text-mintsoft/60" />
            </div>
            <p className="font-display font-medium text-xl text-kraft">
              {quotes.length === 0 ? "No quote requests yet" : "Nothing with this status"}
            </p>
            <p className="text-sm text-kraft/55 max-w-sm">
              {quotes.length === 0
                ? "Every quote request sent from the shop lands here, newest first."
                : "Try another status filter above."}
            </p>
            <Link to="/shop" data-testid="admin-empty-shop-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-mint hover:text-kraft transition-colors">
              Browse the shop <ExternalLink size={11} className="inline" />
            </Link>
          </div>
        ) : (
          <div className="space-y-5">
            {shown.map((q, i) => (
              <motion.div
                key={q.rfq_ref}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.3), ease: [0.16, 1, 0.3, 1] }}
              >
                <QuoteCard q={q} onStatus={onStatus} />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      </AnimatePresence>
    </main>
  );
}
